import { Component, Input, OnChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ContractService } from '../../../service/entity-services/contract.service';

/**
 * SANDBOX-ONLY preview of the nightly contract job, shown on the contract record page.
 *
 * Built for Ray (2026-10-10): for this contract, what will the job do and on which
 * day — auto-renew (and the new expiration date), expire, or send the Notice Deadline
 * Approaching email — and who gets each email, or why someone is skipped.
 *
 * The answer comes from the backend (scheduled-ops sandbox-test/contract-plan), not
 * from rules re-implemented here, so the panel can't disagree with the job.
 *
 * Rendered only when environment.reminderTestTools === true (environment.sandbox-ng.ts
 * only), same gate as the Important Date reminder test panel. Uses signals, so the
 * zoneless app re-renders when the response lands without a manual markForCheck.
 *
 * Reads the SAVED contract. Edits go through the edit page, which reloads this one.
 */
@Component({
  selector: 'app-contract-plan-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  template: `
    <div class="cp-panel">
      <div class="cp-strip">SANDBOX TEST TOOL — what the nightly contract job will do</div>
      <div class="cp-section">
        @if (loading()) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else if (plan()?.error) {
          <p class="cp-bad">{{ plan().error }}</p>
        } @else if (plan()) {
          @switch (plan().rule) {
            @case ('autoRenew') {
              <p class="cp-good">
                Auto-renews on <b>{{ day(plan().eventDate) }}</b>.
                @if (plan().newExpirationDate) { New expiration date: <b>{{ day(plan().newExpirationDate) }}</b>. }
                Status becomes "Active - Auto Renewed" and the Automatic Renewal email goes out.
              </p>
            }
            @case ('expire') {
              <p class="cp-good">
                Expires on <b>{{ day(plan().eventDate) }}</b>. Status becomes "Expired" and the Expired Contract email goes out.
              </p>
            }
            @default {
              <p class="cp-bad">The job will not renew or expire this contract.</p>
            }
          }
          @if (plan().reason) { <p class="cp-hint">{{ plan().reason }}</p> }

          @if (plan().actionDate) {
            <p [class.cp-good]="!plan().actionEmailSent">
              Notice Deadline email: <b>{{ day(plan().actionDate) }}</b>
              @if (plan().actionEmailSent) { — already sent. }
            </p>
            <p class="cp-hint">Expiration date − notice period − review period.</p>
          }
          @if (plan().actionReason) { <p class="cp-hint">{{ plan().actionReason }}</p> }

          <div class="cp-label">Emails go to:</div>
          @if (plan().recipients?.length) {
            <ul class="cp-list">
              @for (r of plan().recipients; track r) { <li>{{ r }}</li> }
            </ul>
          } @else {
            <p class="cp-bad">Nobody — no email will be sent.</p>
          }
          @if (plan().skippedRecipients?.length) {
            <ul class="cp-list cp-hint">
              @for (s of plan().skippedRecipients; track $index) {
                <li>Skipped {{ s.field }}{{ s.value ? ' (' + s.value + ')' : '' }} — {{ s.reason }}</li>
              }
            </ul>
          }
          @if (plan().jobEnabledForOrg === false) {
            <p class="cp-bad">The nightly job is switched off for sandbox — nothing above will happen until it's on.</p>
          } @else if (plan().nextRunAt) {
            <p class="cp-hint">Next nightly run: {{ plan().nextRunAt | date:'MMM d, h:mm a' }}.</p>
          }
          @if (plan().evaluatedAsOf) { <p class="cp-hint">Checked as of {{ day(plan().evaluatedAsOf) }}.</p> }
        }
        <button mat-stroked-button type="button" (click)="refresh()" [disabled]="loading()">Check again</button>
      </div>
    </div>
  `,
  styles: [`
    .cp-panel { border: 2px dashed #e0b100; border-radius: 6px; margin: 16px 0; background: #fffdf3; }
    .cp-strip { background: #fff3cd; color: #6b5200; font-weight: 600; font-size: 12px; letter-spacing: .04em;
                padding: 6px 12px; border-bottom: 1px solid #e0b100; }
    .cp-section { padding: 10px 12px; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
    .cp-label { font-size: 12px; color: #555; margin: 0; }
    .cp-hint { font-size: 12px; color: #777; margin: 0; }
    .cp-good { color: #1b5e20; margin: 0; font-weight: 500; }
    .cp-bad { color: #b71c1c; margin: 0; font-weight: 500; }
    .cp-list { margin: 0; padding-left: 20px; font-size: 13px; }
  `]
})
export class ContractPlanPanelComponent implements OnChanges {
  @Input() contractId = '';

  private contractService = inject(ContractService);

  plan = signal<any>(null);
  loading = signal(false);

  ngOnChanges(): void {
    if (this.contractId) this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.contractService.getSandboxContractPlan(this.contractId).subscribe(res => {
      this.plan.set(res);
      this.loading.set(false);
    });
  }

  /**
   * The backend sends calendar days as YYYY-MM-DD. Format from the string's own
   * parts — new Date('2026-10-03') is UTC midnight and would render Oct 2 in the US.
   */
  day(value: string | null | undefined): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    return m ? `${+m[2]}/${+m[3]}/${m[1]}` : (value ?? '');
  }
}
